import { useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useTodaysScores } from '@/hooks/useTodaysScores';
import { palette } from '@/lib/palette';
import type { TickerScore } from '@/lib/db/types';

/** One full pass through all scores (half the duplicated track width). */
const TICKER_SCROLL_PX_PER_SEC = 120;
const TICKER_MIN_SEC = 6;
const TICKER_MAX_SEC = 22;

export function tickerDurationFromScrollWidth(scrollWidth: number): string {
  const loopWidth = scrollWidth / 2;
  if (loopWidth <= 0) return `${TICKER_MIN_SEC}s`;
  const seconds = Math.max(
    TICKER_MIN_SEC,
    Math.min(TICKER_MAX_SEC, loopWidth / TICKER_SCROLL_PX_PER_SEC),
  );
  return `${seconds.toFixed(1)}s`;
}

interface Props {
  onScoreTap?: (score: TickerScore) => void;
}

type Campus = 'FFX' | 'ARL' | null;

function detectCampus(courseSlug: string | null | undefined): Campus {
  if (!courseSlug) return null;
  if (courseSlug.startsWith('ffx-')) return 'FFX';
  if (courseSlug.startsWith('arl-')) return 'ARL';
  return null;
}

const CAMPUS_COLOR: Record<NonNullable<Campus>, string> = {
  FFX: palette.gold,
  ARL: palette.cream,
};

export function ScoreTicker({ onScoreTap }: Props) {
  const { scores, loading } = useTodaysScores();

  if (loading) {
    return <Strip><span /></Strip>;
  }

  if (scores.length === 0) {
    return (
      <Strip>
        <span
          className="bt-ticker-pulse shrink-0 rounded-full"
          style={{ width: 6, height: 6, background: palette.gold, boxShadow: `0 0 8px ${palette.gold}` }}
        />
        <span style={{ color: palette.gold }}>QUIET DAY</span>
        <span style={{ opacity: 0.5 }}>·</span>
        <span style={{ opacity: 0.85 }}>BE THE FIRST ON THE BOARD ▸</span>
      </Strip>
    );
  }

  return (
    <div
      className="bt-ticker-strip"
      style={{
        background: palette.navy,
        color: palette.cream,
        display: 'flex',
        alignItems: 'stretch',
        overflow: 'hidden',
        position: 'relative',
        fontSize: 12,
        height: 32,
        flexShrink: 0,
      }}
    >
      <Label />
      <div style={{ flex: 1, overflow: 'hidden', minWidth: 0 }}>
        <ScoreTickerTrack scores={scores} onScoreTap={onScoreTap} />
      </div>
    </div>
  );
}

function ScoreTickerTrack({
  scores,
  onScoreTap,
}: {
  scores: TickerScore[];
  onScoreTap?: (score: TickerScore) => void;
}) {
  const trackRef = useRef<HTMLDivElement>(null);
  const [tickerDuration, setTickerDuration] = useState(`${TICKER_MIN_SEC}s`);
  const loop = useMemo(() => [...scores, ...scores], [scores]);

  useLayoutEffect(() => {
    const el = trackRef.current;
    if (!el) return;

    const syncDuration = () => {
      setTickerDuration(tickerDurationFromScrollWidth(el.scrollWidth));
    };

    syncDuration();
    const observer = new ResizeObserver(syncDuration);
    observer.observe(el);
    return () => observer.disconnect();
  }, [scores]);

  return (
    <div
      ref={trackRef}
      className="bt-ticker-track"
      style={{ ['--ticker-duration' as string]: tickerDuration }}
    >
      {loop.map((s, i) => (
        <TickerItem
          key={`${s.id}-${i}`}
          score={s}
          onTap={onScoreTap}
        />
      ))}
    </div>
  );
}

function Strip({ children }: { children: ReactNode }) {
  return (
    <div
      style={{
        background: palette.navy,
        color: palette.cream,
        height: 32,
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '0 14px',
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: '0.08em',
        flexShrink: 0,
      }}
    >
      {children}
    </div>
  );
}

function Label() {
  return (
    <div
      style={{
        background: palette.navyMid,
        padding: '0 12px',
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        fontSize: 10,
        fontWeight: 800,
        letterSpacing: '0.12em',
        color: palette.gold,
        flexShrink: 0,
        borderRight: '1px solid rgba(196,166,98,0.2)',
        zIndex: 2,
      }}
    >
      <span
        className="bt-ticker-pulse"
        style={{
          width: 6,
          height: 6,
          borderRadius: '50%',
          background: palette.gold,
          boxShadow: `0 0 8px ${palette.gold}`,
        }}
      />
      <span>TODAY</span>
      <span style={{ opacity: 0.6 }}>▸</span>
    </div>
  );
}

function TickerItem({
  score,
  onTap,
}: {
  score: TickerScore;
  onTap?: (s: TickerScore) => void;
}) {
  const campus = detectCampus(score.course_slug);
  const chipColor = campus ? CAMPUS_COLOR[campus] : palette.mute;
  const lastName = score.member_name.split(/\s+/).pop() || score.member_name;

  return (
    <div
      onClick={() => onTap?.(score)}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onTap?.(score); }}
      role={onTap ? 'button' : undefined}
      tabIndex={onTap ? 0 : undefined}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '0 16px',
        cursor: onTap ? 'pointer' : 'default',
        height: 32,
        whiteSpace: 'nowrap',
      }}
    >
      <span
        style={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          background: chipColor,
          flexShrink: 0,
        }}
      />
      <span
        style={{
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: '0.04em',
          textTransform: 'uppercase',
          color: palette.cream,
        }}
      >
        {lastName}
      </span>
      <span
        style={{
          fontFamily: '"SF Pro Display", -apple-system, BlinkMacSystemFont, "Inter", system-ui, sans-serif',
          fontSize: 14,
          fontWeight: 700,
          color: palette.gold,
          letterSpacing: '-0.01em',
        }}
      >
        {score.gross_total}
      </span>
      {campus && (
        <span
          style={{
            fontSize: 9,
            fontWeight: 700,
            letterSpacing: '0.1em',
            color: 'rgba(250,250,246,0.5)',
          }}
        >
          {campus}
        </span>
      )}
      <span style={{ color: 'rgba(196,166,98,0.5)', fontSize: 9, marginLeft: 4 }}>
        •
      </span>
    </div>
  );
}
